import { Button, DialogContent, DialogTitle, Stack, Typography, CircularProgress, Alert } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { Project } from '../../Projects/projects';
import { useDispatch } from 'react-redux';
import { applicationSlice } from '../../../redux/ApplicationSlice';
import { ApplicationActions } from '../../../utils/constants';
import { projectSlice } from '../../../redux/ProjectSlice';
import { useCreateProject } from '../../../api/queries';
import { generatePin } from '../../../utils/codeValidation';
import { useState } from 'react';
import { pickProjectVersionFields } from '../../../utils/projectVersions';
import { isBrowserOffline } from '../../../utils/networkStatus';

interface CreateShareCodeDialogProps {
  project: Project | null;
  onClose: () => void;
  open: boolean;
}

const CreateShareCodeDialog = ({ project, onClose }: CreateShareCodeDialogProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [generatedPin] = useState(generatePin()); // Generate PIN once when dialog opens
  const [errorMessage, setErrorMessage] = useState('');
  const createProjectMutation = useCreateProject();

  const handleCancelShareCode = () => {
    onClose();
  };

  const handleCreateShareCode = async () => {
    if (!project) return;

    if (isBrowserOffline()) {
      setErrorMessage(t('createShareCodeDialog.error.offline', 'You appear to be offline. Connect to the internet to create a share code.'));
      return;
    }

    setErrorMessage('');

    try {
      // Serialize the entire project to JSON string
      const projectString = JSON.stringify(project);

      // Call API to create project
      const response = await createProjectMutation.mutateAsync({
        projectString,
        pin: generatedPin,
        title: project.name,
      });
      const versionFields = pickProjectVersionFields(response);

      // Update project with share code, PIN, and set access level
      const updatedProject: Project = {
        ...project,
        ...versionFields,
        shareCode: response.share_code,
        pin: generatedPin,
        accessLevel: 'collaborator',
      };

      // Update Redux state
      dispatch(
        projectSlice.actions.onSetProjectShareCode({
          projectId: project.id,
          shareCode: response.share_code,
          pin: generatedPin,
          ...versionFields,
        })
      );
      dispatch(
        projectSlice.actions.onSetAccessLevel({
          projectId: project.id,
          level: 'collaborator',
        })
      );

      // Close this dialog and open ShareCodeInfoDialog
      onClose();
      dispatch(
        applicationSlice.actions.onOpenDialog({
          ...ApplicationActions.Dialogs.SHARE_CODE_INFO,
          options: {
            ...ApplicationActions.Dialogs.SHARE_CODE_INFO.options,
            project: updatedProject,
            newShareCode: true,
          },
        })
      );
    } catch (error) {
      console.error('Failed to create share code:', error);
      setErrorMessage(t('createShareCodeDialog.error.general', 'Failed to create share code. Please check your connection and try again.'));
    }
  };

  const projectName = project?.name;

  return (
    <>
      <DialogTitle textAlign="center" variant="h5">
        <Trans i18nKey={'createShareCodeDialog.title'}>Share {{ projectName }}</Trans>
      </DialogTitle>
      <DialogContent sx={{ maxHeight: { xs: '70vh', sm: 360 }, overflowY: 'auto' }}>
        <Stack direction="column" spacing={2} marginTop={1} minHeight={250} justifyContent={'space-between'}>
          <Typography variant="panelTitle" sx={{ padding: 2 }}>
            <Trans i18nKey={'createShareCodeDialog.main'}>
              When you create a share code, your project is stored on the Sphero Central platform for access by anyone with the code or direct link.
              Only users with the collaborator PIN can directly edit this project; everyone else can only make a copy.
            </Trans>
          </Typography>

          {(errorMessage || createProjectMutation.isError) && (
            <Alert severity="error">
              {errorMessage || t('createShareCodeDialog.error.general', 'Failed to create share code. Please check your connection and try again.')}
            </Alert>
          )}

          <Stack direction="row" display="flex" justifyContent="space-between" flexShrink={0}>
            <Button onClick={handleCancelShareCode} disabled={createProjectMutation.isPending}>
              <Trans i18nKey="createShareCodeDialog.button.cancel">Cancel</Trans>
            </Button>

            <Button
              onClick={handleCreateShareCode}
              disabled={createProjectMutation.isPending}
              startIcon={createProjectMutation.isPending ? <CircularProgress size={20} /> : null}
            >
              {createProjectMutation.isPending ? (
                <Trans i18nKey="createShareCodeDialog.button.creating">Creating...</Trans>
              ) : (
                <Trans i18nKey="createShareCodeDialog.button.create">Create</Trans>
              )}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </>
  );
};

export default CreateShareCodeDialog;
